import axios from 'axios';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import { useWagmiWallet } from './WagmiWalletContext';
import { getCategoryForContract } from '../../utils/contractCategorization';
import { ABICacheManager } from '../../utils/abiCacheManager';
import logger from '../../utils/logger';
import { getEnv } from '../../utils/envUtils';

/* Types */
type DeploymentDetails = {
    multisigs: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    core: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    proxied: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    liquidity?: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    helpers?: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    charity?: Record<string, { addressForUI: string; addressForABI: string; abi: string }>;
    DEXs?: Record<string, { addressForUI: string; addressForABI?: string; abi?: string; description?: string }>;
};

export type Contract = {
    name: string;
    address: string;
    abi: any[];
    type: 'core' | 'proxy' | 'multisig' | 'utility' | 'liquidity' | 'charity' | 'other';
};

/* Interface */
interface ContractContextType {
    contracts: any[];
    deploymentDetails: DeploymentDetails | null;
    isLoading: boolean;
    error: string | null;
    memerCoreAddress: string;
    selectedContract: any;
    setSelectedContract: (contract: any) => void;
    owners: string[];
    protocolAddresses: Array<{ name: string; address: string; description?: string }>;
    loadContracts: () => Promise<void>;
    fetchOwnersFromContract: () => Promise<void>;
    forceLoadingComplete: () => void;
}

const ContractContext = createContext<ContractContextType | undefined>(undefined);

// Control debug logging
const DEBUG = false; // Disable debug logging for production
const log = (...args: any[]) => {
    if (DEBUG) logger.log(...args);
};

// Time limits to prevent infinite loading
const CONTRACT_LOADING_TIMEOUT = 10000; // 10 seconds max wait for contract loading
const ABI_LOADING_TIMEOUT = 5000; // 5 seconds max wait for ABI loading

export const ContractProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { provider, signer, chainId: chainIdNumber } = useWagmiWallet();
    const chainId = chainIdNumber?.toString();
    const [contracts, setContracts] = useState<any[]>([]);
    const [deploymentDetails, setDeploymentDetails] = useState<DeploymentDetails | null>(null);
    const [memerCoreAddress, setMemerCoreAddress] = useState<string>("");
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const [owners, setOwners] = useState<string[]>([]);
    const [protocolAddresses, setProtocolAddresses] = useState<Array<{ name: string; address: string; description?: string }>>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isContractLoading, setIsContractLoading] = useState(false);
    const [loadStartTime, setLoadStartTime] = useState<number | null>(null);
    const [contractsLoaded, setContractsLoaded] = useState(false);

    // Force loading to complete (failsafe)
    const forceLoadingComplete = useCallback(() => {
        log('🚨 Forcing loading complete (timeout or manual)');
        setIsLoading(false);
        setIsContractLoading(false);
        setError(prev => prev || 'Contract loading timed out - forcing UI to display');

        // If no contracts loaded, add a placeholder
        if (contracts.length === 0) {
            setContracts([{
                name: 'PlaceholderContract',
                address: '0x0000000000000000000000000000000000000000',
                abi: [],
                type: 'other'
            }]);
        }
    }, [contracts.length]);

    /* Import utility functions */
    // Enhanced ABI loading with cache busting, validation, and fallback mechanisms
    const loadAbi = useCallback(async (abiName: string, addressForABI: string, chainId: string): Promise<any> => {
        try {
            // Set up timeout for ABI loading
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error(`ABI loading timed out for ${abiName}`)), ABI_LOADING_TIMEOUT);
            });

            // Attempt to load ABI with enhanced logic
            const abiPromise = (async () => {
                const abiPath = `/abi/${chainId}/${abiName}ABI.json`;
                let abi: any[] = [];
                let cacheUsed = false;

                // Step 1: Try localStorage cache first (with validation)
                try {
                    const cacheKey = ABICacheManager.getCacheKey(chainId, abiName);
                    const cachedAbi = localStorage.getItem(cacheKey);

                    if (cachedAbi) {
                        const parsed = JSON.parse(cachedAbi);
                        if (parsed.abi && Array.isArray(parsed.abi)) {
                            // Validate cached ABI completeness
                            if (ABICacheManager.validateABICompleteness(parsed.abi, abiName)) {
                                // Silent return for cached ABIs - no logging needed
                                return parsed.abi;
                            } else {
                                ABICacheManager.clearContractABICache(chainId, abiName);
                            }
                        }
                    }
                } catch (e) {
                    ABICacheManager.clearContractABICache(chainId, abiName);
                }

                // Step 2: Load from appropriate source based on chainId
                const isDev = import.meta.env.DEV;

                switch (chainId) {
                    case "31337": // Anvil local or fork
                    case "31337-fork": // Handle both the same way
                        if (isDev) {
                            logger.log(`📁 Loading ABI for chainId 31337 (Anvil local or fork): ${abiName}`);
                        }
                        abi = await loadAnvilAbiWithCacheBusting("31337", abiName);
                        break;
                    case "8453": // Base mainnet
                        if (isDev) {
                            logger.log(`📁 Loading ABI for chainId 8453 (Base mainnet) from local files first: ${abiName}`);
                        }
                        // CRITICAL FIX: Try local files first (same as Anvil), then fallback to BaseScan
                        try {
                            abi = await loadAnvilAbiWithCacheBusting("8453", abiName);
                            // Validate we got a complete ABI
                            if (!abi || !Array.isArray(abi) || abi.length === 0) {
                                logger.warn(`Local ABI file empty or invalid for ${abiName}, falling back to BaseScan`);
                                abi = await loadBaseABIWithCacheBusting(addressForABI);
                            } else {
                                logger.log(`✅ Loaded ABI from local file for ${abiName} (${abi.length} items)`);
                            }
                        } catch (localError) {
                            logger.warn(`Failed to load local ABI for ${abiName}, falling back to BaseScan:`, localError);
                            abi = await loadBaseABIWithCacheBusting(addressForABI);
                        }
                        break;
                    case "84532": // Base Sepolia
                        if (isDev) {
                            logger.log(`🌐 Loading ABI from Sepolia with cache busting for ${abiName}`);
                        }
                        abi = await loadSepoliaABIWithCacheBusting(addressForABI);
                        break;
                    default:
                        logger.log(`🔍 Unsupported chainId: ${chainId}, attempting to load from public ABI folder...`);
                        try {
                            // Add cache busting to the request
                            const cacheBustedUrl = ABICacheManager.addCacheBuster(abiPath);
                            const response = await fetch(cacheBustedUrl, {
                                headers: {
                                    'Accept': 'application/json',
                                    'Content-Type': 'application/json',
                                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                                    'Pragma': 'no-cache',
                                    'Expires': '0'
                                }
                            });

                            if (!response.ok) {
                                logger.warn(`ABI fetch failed (Status: ${response.status}) for ${abiName}`);
                                return generateFallbackAbi(abiName);
                            }

                            const data = await response.json();

                            // Check if the data is already an array (direct ABI) or has an abi property
                            if (Array.isArray(data)) {
                                abi = data;
                            } else if (data.abi && Array.isArray(data.abi)) {
                                abi = data.abi;
                            } else {
                                logger.warn(`Invalid ABI format for ${abiName}:`, data);
                                return generateFallbackAbi(abiName);
                            }
                        } catch (fetchError) {
                            logger.error(`Error fetching ABI for ${abiName}:`, fetchError);
                            return generateFallbackAbi(abiName);
                        }
                }

                // Step 3: Validate loaded ABI
                if (!abi || !Array.isArray(abi) || abi.length === 0) {
                    logger.warn(`❌ Empty or invalid ABI loaded for ${abiName}, using fallback`);
                    abi = generateFallbackAbi(abiName);
                } else if (!ABICacheManager.validateABICompleteness(abi, abiName)) {
                    logger.warn(`❌ ABI validation failed for ${abiName}, attempting fallback`);
                    const fallbackAbi = generateFallbackAbi(abiName);

                    // If fallback has more functions, use it; otherwise keep the loaded ABI
                    const loadedFunctions = abi.filter(item => item.type === 'function').length;
                    const fallbackFunctions = fallbackAbi.filter(item => item.type === 'function').length;

                    if (fallbackFunctions > loadedFunctions) {
                        logger.log(`Using fallback ABI (${fallbackFunctions} vs ${loadedFunctions} functions)`);
                        abi = fallbackAbi;
                    } else {
                        logger.log(`Keeping loaded ABI despite validation failure (${loadedFunctions} functions)`);
                    }
                }

                // Step 4: Cache the validated ABI
                try {
                    const cacheKey = ABICacheManager.getCacheKey(chainId, abiName);
                    localStorage.setItem(cacheKey, JSON.stringify({ abi, timestamp: Date.now() }));
                    logger.log(`💾 Cached validated ABI for ${abiName}`);
                } catch (cacheError) {
                    logger.warn(`Failed to cache ABI for ${abiName}:`, cacheError);
                }

                // Step 5: Final logging
                const functions = abi.filter(item => item.type === 'function');
                logger.log(`✅ Successfully loaded ABI for ${abiName}: ${abi.length} total items, ${functions.length} functions`);

                if (abiName === 'LPTokenLocker') {
                    const requiredFunctions = ['ASSET_RECOVERY_ROLE', 'getLockedAsset', 'getLockedAssetsCount', 'getOriginalAmount'];
                    const foundFunctions = requiredFunctions.filter(funcName =>
                        functions.some(f => f.name === funcName)
                    );
                    logger.log(`🔍 LPTokenLocker validation: Found ${foundFunctions.length}/${requiredFunctions.length} required functions: ${foundFunctions.join(', ')}`);
                }

                return abi;
            })();

            // Race between the ABI loading and timeout
            return await Promise.race([abiPromise, timeoutPromise]) as any[];
        } catch (error) {
            logger.error(`❌ Error loading ABI for ${abiName}:`, error);
            logger.log(`🔄 Falling back to generated ABI for ${abiName}`);
            return generateFallbackAbi(abiName);
        }
    }, []);

    const saveAbiFile = useCallback(async (chainId: string, abiName: string, abiData: any) => {
        try {
            // Save to both localStorage and file path
            localStorage.setItem(`abi-${chainId}-${abiName}`, JSON.stringify({ abi: abiData }));

            const filePath = `/abi/${chainId}/${abiName}ABI.json`;
            localStorage.setItem(filePath, JSON.stringify({ abi: abiData }, null, 2));
        } catch (error) {
            log(`Error saving ABI:`, error);
        }
    }, []);

    // Generate a fallback ABI with common functions based on contract name
    const generateFallbackAbi = (abiName: string): any[] => {
        logger.log(`Generating fallback ABI for ${abiName}`);

        // Basic ERC20 functions that most tokens implement
        const erc20Functions = [
            {
                "type": "function",
                "name": "balanceOf",
                "inputs": [{ "name": "account", "type": "address" }],
                "outputs": [{ "name": "", "type": "uint256" }],
                "stateMutability": "view"
            },
            {
                "type": "function",
                "name": "transfer",
                "inputs": [
                    { "name": "recipient", "type": "address" },
                    { "name": "amount", "type": "uint256" }
                ],
                "outputs": [{ "name": "", "type": "bool" }],
                "stateMutability": "nonpayable"
            },
            {
                "type": "function",
                "name": "name",
                "inputs": [],
                "outputs": [{ "name": "", "type": "string" }],
                "stateMutability": "view"
            },
            {
                "type": "function",
                "name": "symbol",
                "inputs": [],
                "outputs": [{ "name": "", "type": "string" }],
                "stateMutability": "view"
            },
            {
                "type": "function",
                "name": "decimals",
                "inputs": [],
                "outputs": [{ "name": "", "type": "uint8" }],
                "stateMutability": "view"
            }
        ];

        // MultiSig wallet functions
        const multiSigFunctions = [
            {
                "type": "function",
                "name": "getOwners",
                "inputs": [],
                "outputs": [{ "name": "", "type": "address[]" }],
                "stateMutability": "view"
            },
            {
                "type": "function",
                "name": "execTransaction",
                "inputs": [
                    { "name": "to", "type": "address" },
                    { "name": "value", "type": "uint256" },
                    { "name": "data", "type": "bytes" },
                    { "name": "operation", "type": "uint8" },
                    { "name": "safeTxGas", "type": "uint256" },
                    { "name": "baseGas", "type": "uint256" },
                    { "name": "gasPrice", "type": "uint256" },
                    { "name": "gasToken", "type": "address" },
                    { "name": "refundReceiver", "type": "address" },
                    { "name": "signatures", "type": "bytes" }
                ],
                "outputs": [{ "name": "success", "type": "bool" }],
                "stateMutability": "nonpayable"
            }
        ];

        // Timelock controller functions
        const timelockFunctions = [
            {
                "type": "function",
                "name": "schedule",
                "inputs": [
                    { "name": "target", "type": "address" },
                    { "name": "value", "type": "uint256" },
                    { "name": "data", "type": "bytes" },
                    { "name": "predecessor", "type": "bytes32" },
                    { "name": "salt", "type": "bytes32" },
                    { "name": "delay", "type": "uint256" }
                ],
                "outputs": [],
                "stateMutability": "nonpayable"
            },
            {
                "type": "function",
                "name": "execute",
                "inputs": [
                    { "name": "target", "type": "address" },
                    { "name": "value", "type": "uint256" },
                    { "name": "data", "type": "bytes" },
                    { "name": "predecessor", "type": "bytes32" },
                    { "name": "salt", "type": "bytes32" }
                ],
                "outputs": [],
                "stateMutability": "payable"
            }
        ];

        // Determine which functions to include based on contract name
        const lowerName = abiName.toLowerCase();
        let fallbackAbi: any[] = [];

        if (lowerName.includes('multisig') || lowerName.includes('wallet')) {
            fallbackAbi = [...multiSigFunctions];
        } else if (lowerName.includes('timelock') || lowerName.includes('controller')) {
            fallbackAbi = [...timelockFunctions];
        } else if (lowerName.includes('token') || lowerName.includes('erc20')) {
            fallbackAbi = [...erc20Functions];
        } else {
            // Include a mix of common functions as a last resort
            fallbackAbi = [
                ...erc20Functions.slice(0, 2),
                multiSigFunctions[0],
                timelockFunctions[0]
            ];
        }

        logger.log(`Generated fallback ABI with ${fallbackAbi.length} functions`);
        return fallbackAbi;
    };

    // Anvil ABI loading with cache busting
    const loadAnvilAbiWithCacheBusting = useCallback(async (chainId: string, abiName: string): Promise<any> => {
        try {
            // First try to load from the abi directory with cache busting
            const abiPath = `/abi/${chainId}/${abiName}ABI.json`;
            const cacheBustedUrl = ABICacheManager.addCacheBuster(abiPath);

            try {
                logger.log(`🔄 Attempting to load ABI with cache busting from: ${cacheBustedUrl}`);
                const response = await fetch(cacheBustedUrl, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });

                if (response.ok) {
                    const data = await response.json();

                    // Log the raw data for debugging
                    logger.log(`📄 Raw ABI data for ${abiName}:`, JSON.stringify(data).substring(0, 100) + "...");

                    // Check if this is a direct ABI array or if it has an abi property
                    if (Array.isArray(data)) {
                        logger.log(`✅ Loaded ABI for ${abiName} (direct array format) - ${data.filter((item: any) => item.type === 'function').length} functions`);
                        await saveAbiFile(chainId, abiName, data);
                        return data;
                    } else if (data.abi && Array.isArray(data.abi)) {
                        logger.log(`✅ Loaded ABI for ${abiName} (object with abi property) - ${data.abi.filter((item: any) => item.type === 'function').length} functions`);
                        await saveAbiFile(chainId, abiName, data.abi);
                        return data.abi;
                    } else {
                        // Try to extract any array property that might be the ABI
                        const possibleAbiProps = Object.entries(data)
                            .filter(([_, value]) => Array.isArray(value) && value.length > 0)
                            .map(([key, value]) => ({ key, value }));

                        if (possibleAbiProps.length > 0) {
                            // Use the first array property as the ABI
                            const firstArrayProp = possibleAbiProps[0];
                            logger.log(`🔧 Using ${firstArrayProp.key} property as ABI for ${abiName}`);
                            await saveAbiFile(chainId, abiName, firstArrayProp.value);
                            return firstArrayProp.value;
                        }

                        logger.warn(`❌ ABI for ${abiName} has unexpected format:`, data);
                        return generateFallbackAbi(abiName);
                    }
                } else {
                    logger.warn(`❌ Failed to load ABI from ${cacheBustedUrl}. Status: ${response.status}`);
                }
            } catch (abiError) {
                logger.warn(`❌ Error loading from ABI directory with cache busting: ${abiError}`);
                // Continue to fallback
            }

            // Fallback to the out directory with cache busting
            const outPath = `/out/${abiName}.json`;
            const cacheBustedOutUrl = ABICacheManager.addCacheBuster(outPath);
            logger.log(`🔄 Attempting to load ABI from fallback path with cache busting: ${cacheBustedOutUrl}`);

            try {
                const outResponse = await fetch(cacheBustedOutUrl, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });

                if (!outResponse.ok) {
                    logger.warn(`❌ Fetch failed for ${cacheBustedOutUrl}. Status: ${outResponse.status}`);
                    return generateFallbackAbi(abiName);
                }

                const outData = await outResponse.json();
                if (!outData.abi || !Array.isArray(outData.abi) || outData.abi.length === 0) {
                    logger.warn(`❌ Invalid or empty ABI structure in ${cacheBustedOutUrl}`);
                    return generateFallbackAbi(abiName);
                }

                logger.log(`✅ Loaded ABI from out directory for ${abiName} - ${outData.abi.filter((item: any) => item.type === 'function').length} functions`);
                await saveAbiFile(chainId, abiName, outData.abi);
                return outData.abi;
            } catch (outError) {
                logger.warn(`❌ Error loading from out directory: ${outError}`);
                return generateFallbackAbi(abiName);
            }
        } catch (error) {
            logger.error(`❌ Error loading ABI for ${abiName}:`, error);
            return generateFallbackAbi(abiName);
        }
    }, [saveAbiFile, generateFallbackAbi]);

    const loadAnvilAbi = useCallback(async (chainId: string, abiName: string): Promise<any> => {
        try {
            // First try to load from the abi directory which has the properly formatted ABIs
            const abiPath = `/abi/${chainId}/${abiName}ABI.json`;
            try {
                logger.log(`Attempting to load ABI from: ${abiPath}`);
                const response = await fetch(abiPath, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();

                    // Log the raw data for debugging
                    logger.log(`Raw ABI data for ${abiName}:`, JSON.stringify(data).substring(0, 100) + "...");

                    // Check if this is a direct ABI array or if it has an abi property
                    if (Array.isArray(data)) {
                        logger.log(`Loaded ABI for ${abiName} (direct array format)`);
                        await saveAbiFile(chainId, abiName, data);
                        return data;
                    } else if (data.abi && Array.isArray(data.abi)) {
                        logger.log(`Loaded ABI for ${abiName} (object with abi property)`);
                        await saveAbiFile(chainId, abiName, data.abi);
                        return data.abi;
                    } else {
                        // Try to extract any array property that might be the ABI
                        const possibleAbiProps = Object.entries(data)
                            .filter(([_, value]) => Array.isArray(value) && value.length > 0)
                            .map(([key, value]) => ({ key, value }));

                        if (possibleAbiProps.length > 0) {
                            // Use the first array property as the ABI
                            const firstArrayProp = possibleAbiProps[0];
                            logger.log(`Using ${firstArrayProp.key} property as ABI for ${abiName}`);
                            await saveAbiFile(chainId, abiName, firstArrayProp.value);
                            return firstArrayProp.value;
                        }

                        logger.warn(`ABI for ${abiName} has unexpected format:`, data);
                        return generateFallbackAbi(abiName);
                    }
                } else {
                    logger.warn(`Failed to load ABI from ${abiPath}. Status: ${response.status}`);
                }
            } catch (abiError) {
                logger.warn(`Error loading from ABI directory: ${abiError}`);
                // Continue to fallback
            }

            // Fallback to the out directory
            const outPath = `/out/${abiName}.json`;
            logger.log(`Attempting to load ABI from fallback path: ${outPath}`);
            try {
                const outResponse = await fetch(outPath, {
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    }
                });
                if (!outResponse.ok) {
                    logger.warn(`Fetch failed for ${outPath}. Status: ${outResponse.status}`);
                    return generateFallbackAbi(abiName);
                }
                const outData = await outResponse.json();
                if (!outData.abi || !Array.isArray(outData.abi) || outData.abi.length === 0) {
                    logger.warn(`Invalid or empty ABI structure in ${outPath}`);
                    return generateFallbackAbi(abiName);
                }
                logger.log(`Loaded ABI from out directory for ${abiName}`);
                await saveAbiFile(chainId, abiName, outData.abi);
                return outData.abi;
            } catch (outError) {
                logger.warn(`Error loading from out directory: ${outError}`);
                return generateFallbackAbi(abiName);
            }
        } catch (error) {
            log(`Error loading ABI for ${abiName}:`, error);
            return [];
        }
    }, [saveAbiFile]);

    // Base mainnet ABI loading with cache busting
    const loadBaseABIWithCacheBusting = useCallback(async (address: string): Promise<any> => {
        // Skip actual API call for testing
        if (address === '0x0000000000000000000000000000000000000000') {
            return [];
        }

        try {
            logger.log(`🌐 Loading ABI from BaseScan with cache busting for address: ${address}`);
            const response = await axios.get(`https://api.basescan.org/api`, {
                params: {
                    module: 'contract',
                    action: 'getabi',
                    address: address,
                    apikey: getEnv('BASESCAN_API_KEY'),
                    // Cache busting via query parameter (CORS-safe)
                    _t: Date.now()
                }
                // Note: No custom headers - BaseScan CORS doesn't allow Cache-Control header
            });

            if (response.data.status !== '1') throw new Error('BaseScan ABI fetch failed');
            const abi = JSON.parse(response.data.result);
            logger.log(`✅ Loaded ABI from BaseScan - ${abi.filter((item: any) => item.type === 'function').length} functions`);
            return abi;
        } catch (error) {
            logger.error('BaseScan ABI fetch error:', error);
            // Return minimal ABI to allow progress
            return [];
        }
    }, []);

    // Sepolia ABI loading with cache busting
    const loadSepoliaABIWithCacheBusting = useCallback(async (address: string): Promise<any> => {
        logger.log(`🌐 Loading ABI from Sepolia with cache busting for address: ${address}`);
        return await loadBaseABIWithCacheBusting(address);
    }, [loadBaseABIWithCacheBusting]);

    // Legacy functions for backward compatibility
    async function loadBaseScanAbi(address: string): Promise<any> {
        return await loadBaseABIWithCacheBusting(address);
    }

    async function loadSepoliaAbi(address: string): Promise<any> {
        return await loadSepoliaABIWithCacheBusting(address);
    }

    const loadForkedAbi = useCallback(async (chainId: string, abiName: string, address: string): Promise<any> => {
        try {
            return await loadAnvilAbi(chainId, abiName);
        } catch (localError) {
            log(`Failed to load forked ABI locally: ${String(localError)}`);
            try {
                return await loadBaseScanAbi(address);
            } catch (scanError) {
                logger.error(`Error loading ABI from BaseScan: ${String(scanError)}`);
                return [];
            }
        }
    }, [loadAnvilAbi]);

    // Debug utility function to safely inspect file contents
    const inspectFileContent = async (filePath: string) => {
        try {
            const response = await fetch(filePath);
            const status = response.status;
            const statusText = response.statusText;

            if (!response.ok) {
                logger.error(`File fetch failed: ${status} ${statusText}`);
                return null;
            }

            // Get raw text
            const text = await response.text();

            // Log file metadata
            logger.log(`File: ${filePath}, Size: ${text.length} bytes`);

            // If file is empty or just whitespace
            if (!text || text.trim() === '') {
                logger.error(`File is empty or contains only whitespace`);
                return null;
            }

            // Try to safely show first few characters for debugging
            // logger.log(`First 100 chars: "${text.substring(0, 100).replace(/\n/g, '\\n').replace(/\r/g, '\\r')}..."`);

            // Try JSON parsing
            try {
                const json = JSON.parse(text);
                // logger.log(`JSON parsed successfully with keys: ${Object.keys(json).join(', ')}`);
                return json;
            } catch (parseError) {
                logger.error(`JSON parse error: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`);
                return null;
            }
        } catch (error) {
            logger.error(`File inspection error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return null;
        }
    };

    const loadDeploymentDetails = useCallback(async (chainId: string | null): Promise<DeploymentDetails | null> => {
        if (!chainId) {
            log("No chain ID provided to loadDeploymentDetails");
            return null;
        }

        try {
            const filePath = `/deployments/deployment-details-${chainId}.json`;
            // logger.log(`Attempting to fetch deployment details from: ${filePath}`);

            // First, try to diagnose the file
            // logger.log(`Inspecting file...`);
            const diagnosis = await inspectFileContent(filePath);

            if (diagnosis) {
                // logger.log(`File inspection successful, returning parsed data`);
                return diagnosis as DeploymentDetails;
            }

            logger.warn(`File diagnosis failed, attempting normal fetch...`);

            // Fall back to regular loading
            const response = await fetch(filePath);

            if (!response.ok) {
                logger.error(`Failed to load deployment details (Status: ${response.status})`);
                logger.error(`Attempted path: ${filePath}`);

                // Fallback for localhost/development
                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                    logger.warn('Falling back to local deployment details');
                    // Try to create a minimal valid structure
                    return {
                        multisigs: {},
                        core: {},
                        proxied: {},
                        liquidity: {},
                        helpers: {}
                    };
                }

                return null;
            }

            const rawText = await response.text();

            // Log the raw content for debugging
            // logger.log(`Raw file content (first 100 chars): "${rawText.substring(0, 100).replace(/\n/g, '\\n')}..."`);

            // Additional safeguards for parsing
            if (!rawText || rawText.trim() === '') {
                logger.error('Deployment details file is empty');

                // For development environments, try to recover with a default structure
                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                    logger.warn('Creating placeholder deployment details for localhost');
                    return {
                        multisigs: {},
                        core: {},
                        proxied: {},
                        liquidity: {},
                        helpers: {}
                    };
                }

                return null;
            }

            try {
                const json = JSON.parse(rawText);

                // Validate basic structure
                if (!json.core && !json.multisigs && !json.proxied) {
                    logger.error('Invalid deployment details structure:', json);
                    return null;
                }

                return json;
            } catch (parseError) {
                logger.error('JSON Parsing Error:', {
                    message: parseError instanceof Error ? parseError.message : 'Unknown parsing error',
                    rawContent: rawText.substring(0, 100) // Only show a substring for safety
                });

                // For development environments, recover with a default structure
                if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                    logger.warn('Creating placeholder deployment details for localhost');
                    return {
                        multisigs: {},
                        core: {},
                        proxied: {},
                        liquidity: {},
                        helpers: {}
                    };
                }

                return null;
            }
        } catch (error) {
            logger.error("Comprehensive error fetching deployment details:", {
                error: error instanceof Error ? error.message : 'Unknown error',
                chainId
            });
            return null;
        }
    }, []);

    // Then replace the existing getCategoryForContract with:
    const categorizeContract = useCallback((name: string): Contract['type'] => {
        return getCategoryForContract(name);
    }, []);

    const loadContracts = useCallback(async () => {
        // Skip if already loaded
        if (contractsLoaded) {
            // logger.log('Contracts already loaded, skipping');
            return;
        }
        // Prevent multiple simultaneous loads
        if (isContractLoading) {
            log('Contract loading already in progress');
            return;
        }

        // Skip if we don't have a valid chainId yet
        if (!chainId || chainId === '0') {
            log('Invalid chain ID, skipping contract load', { chainId });
            setIsLoading(false); // Still set loading to false to allow UI to render
            return;
        }

        log('Loading contracts for chainId:', chainId);
        setIsContractLoading(true);
        setIsLoading(true);
        setError(null);
        setLoadStartTime(Date.now());

        try {
            const details = await loadDeploymentDetails(chainId);

            if (!details) {
                const isLocalhost = window.location.hostname === "localhost" ||
                    window.location.hostname === "127.0.0.1";

                if (isLocalhost) {
                    log('Creating placeholder deployment details for localhost');
                    setDeploymentDetails({
                        multisigs: {},
                        core: {},
                        proxied: {},
                        liquidity: {},
                        helpers: {}
                    });
                } else {
                    logger.error(`No deployment details found for chain ID ${chainId}`);
                    setError(`No deployment details found for chain ID ${chainId}`);
                }
                setIsLoading(false);
                setIsContractLoading(false);
                return;
            }

            setDeploymentDetails(details);

            // Extract protocol addresses from DEXs section
            if (details.DEXs) {
                const extractedProtocolAddresses = Object.entries(details.DEXs).map(([name, entry]) => ({
                    name,
                    address: entry.addressForUI,
                    description: entry.description
                }));
                setProtocolAddresses(extractedProtocolAddresses);
            } else {
                setProtocolAddresses([]);
            }

            const loadedContracts: Contract[] = [];

            // Track MemerCore address locally (state updates are async)
            let foundMemerCoreAddress = '';

            // Modified memerCoreAddress detection with fallback
            if (details.core?.memerCore?.addressForUI) {
                foundMemerCoreAddress = details.core.memerCore.addressForUI;
                setMemerCoreAddress(details.core.memerCore.addressForUI);
                logger.log("✅ Setting MemerCore address:", details.core.memerCore.addressForUI);
            }

            // Memer ecosystem contracts only (require ABIs)
            const memerEcosystemContracts = {
                ...details.multisigs,
                ...details.core,
                ...details.proxied,
                ...(details.liquidity || {}),
                ...(details.helpers || {}),
                ...(details.charity || {})
            };

            // DEXs are handled separately as protocol addresses only

            const contractEntries = Object.entries(memerEcosystemContracts);
            log('Total Contracts to Load:', contractEntries.length);

            // Detailed logging of contract detection
            const contractCategories: Record<string, string[]> = {};

            // Use a small batch size for loading contracts to avoid timeouts
            const BATCH_SIZE = 5;
            for (let i = 0; i < contractEntries.length; i += BATCH_SIZE) {
                const batch = contractEntries.slice(i, i + BATCH_SIZE);

                // Process each batch in parallel
                const batchResults = await Promise.all(
                    batch.map(async ([name, entry]: [string, any]) => {
                        if (!entry.addressForUI) {
                            log(`Skipping contract ${name}: No address found`);
                            return null;
                        }

                        // Handle contracts without ABI (like DEX protocol addresses)
                        let abi: any[] = [];
                        if (entry.abi && typeof entry.abi === 'string') {
                            try {
                                abi = await loadAbi(entry.abi, entry.addressForABI, chainId);
                            } catch (abiError) {
                                log(`Failed to load ABI for ${name}, using empty ABI:`, abiError);
                                abi = [];
                            }
                        } else {
                            log(`Contract ${name} has no abi field, using empty ABI`);
                            abi = [];
                        }

                        // Special handling for MultiSigWallet - if ABI is empty, use a hardcoded fallback
                        if ((!abi || !Array.isArray(abi) || abi.length === 0) &&
                            (name === 'deploymentMultiSig' || name === 'MultiSigWallet' || name.includes('MultiSig'))) {
                            logger.log(`Using hardcoded fallback ABI for ${name}`);
                            abi = [
                                {
                                    "type": "function",
                                    "name": "getOwners",
                                    "inputs": [],
                                    "outputs": [{ "name": "", "type": "address[]" }],
                                    "stateMutability": "view"
                                },
                                {
                                    "type": "function",
                                    "name": "getThreshold",
                                    "inputs": [],
                                    "outputs": [{ "name": "", "type": "uint256" }],
                                    "stateMutability": "view"
                                },
                                {
                                    "type": "function",
                                    "name": "execTransaction",
                                    "inputs": [
                                        { "name": "to", "type": "address" },
                                        { "name": "value", "type": "uint256" },
                                        { "name": "data", "type": "bytes" },
                                        { "name": "operation", "type": "uint8" },
                                        { "name": "safeTxGas", "type": "uint256" },
                                        { "name": "baseGas", "type": "uint256" },
                                        { "name": "gasPrice", "type": "uint256" },
                                        { "name": "gasToken", "type": "address" },
                                        { "name": "refundReceiver", "type": "address" },
                                        { "name": "signatures", "type": "bytes" }
                                    ],
                                    "outputs": [{ "name": "success", "type": "bool" }],
                                    "stateMutability": "nonpayable"
                                }
                            ];
                        }

                        if (abi && Array.isArray(abi)) {
                            // Reduced logging - only show summary
                            if (DEBUG) logger.log(`Loaded ABI for ${name}: ${abi.filter(item => item.type === 'function').length} functions`);

                            // Categorize the contract
                            const contractType = getCategoryForContract(name);

                            // Track contract categories for logging
                            if (!contractCategories[contractType]) {
                                contractCategories[contractType] = [];
                            }
                            contractCategories[contractType].push(name);

                            const contractInfo = {
                                name,
                                address: entry.addressForUI,
                                abi,
                                type: contractType
                            };
                            return contractInfo;
                        }
                        return null;
                    })
                );

                // Add valid contracts to the result
                loadedContracts.push(...batchResults.filter(Boolean) as Contract[]);

                // Allow UI to render after initial batch
                if (i === 0 && loadedContracts.length > 0) {
                    setContracts([...loadedContracts]);

                    // Set initial selected contract as soon as first batch loads
                    if (!selectedContract && loadedContracts.length > 0) {
                        logger.log("Setting initial selected contract:", loadedContracts[0].name);
                        logger.log("Contract ABI length:", loadedContracts[0].abi.length);

                        // Log the functions in the ABI
                        const functions = loadedContracts[0].abi.filter(item => item.type === 'function');
                        logger.log(`Contract has ${functions.length} functions in ABI`);
                        if (functions.length > 0) {
                            logger.log("First few functions:", functions.slice(0, 3).map(f => f.name));
                        } else {
                            logger.log("WARNING: No functions found in ABI!");
                        }

                        setSelectedContract(loadedContracts[0]);
                    }
                }

                // Check for timeout after each batch
                if (loadStartTime && (Date.now() - loadStartTime > CONTRACT_LOADING_TIMEOUT)) {
                    log('⏱️ Contract loading timeout reached - stopping early');
                    break;
                }
            }

            // Log detailed contract categories
            log('Detected Contract Categories:', contractCategories);

            log(`Loaded ${loadedContracts.length} contracts`);
            setContracts(loadedContracts);

            // Fallback MemerCore address detection
            if (!memerCoreAddress) {
                const memerCoreContract = loadedContracts.find(c =>
                    c.name.toLowerCase() === 'memercore' ||
                    (c.name.toLowerCase().includes('memer') && c.name.toLowerCase().includes('core'))
                );

                if (memerCoreContract) {
                    // logger.log("Found MemerCore by name search:", memerCoreContract.address);
                    setMemerCoreAddress(memerCoreContract.address);
                } else {
                    logger.warn("MemerCore address not found in deployment details or contract list");
                }
            }

            // Temporary Logging
            logger.log(`Contract loading complete: ${loadedContracts.length} contracts`);
            logger.log("First few contracts:", loadedContracts.slice(0, 2).map(c => ({
                name: c.name,
                address: c.address.substring(0, 10) + "...",
                type: c.type
            })));
            logger.log("MemerCore address:", foundMemerCoreAddress);

            if (loadedContracts.length > 0) {
                // Set loading to false as soon as we have some contracts
                setIsLoading(false);
                setIsContractLoading(false);
            }
        } catch (err: any) {
            logger.error("Error loading contracts:", err.message);
            setError(err.message || "Failed to load contracts");
        } finally {
            // Ensure loading is marked as complete
            setIsLoading(false);
            setIsContractLoading(false);
            setLoadStartTime(null);
        }
        setContractsLoaded(true);
    }, [
        chainId,
        isContractLoading,
        loadDeploymentDetails,
        loadAbi,
        selectedContract,
        loadStartTime
    ]); // Removed getCategoryForContract from dependencies

    const fetchOwnersFromContract = useCallback(async () => {
        if (selectedContract && signer) {
            // Reduced logging - only essential information
            if (DEBUG) {
                logger.log("Attempting to fetch owners from contract:", selectedContract.name);
                logger.log("Contract address:", selectedContract.address);
            }

            try {
                // Filter the ABI to find the getOwners function
                const ownerMethod = selectedContract.abi.find(
                    (item: any) => item.type === 'function' && item.name === 'getOwners'
                );

                // Only attempt to fetch owners if the contract has the getOwners method
                if (ownerMethod) {
                    const contract = new ethers.Contract(
                        selectedContract.address,
                        selectedContract.abi,
                        signer
                    );

                    const fetchedOwners: string[] = await contract.getOwners();
                    if (DEBUG) logger.log("Successfully fetched owners:", fetchedOwners);
                    setOwners(fetchedOwners);
                    log("Fetched owners:", fetchedOwners.length);
                } else {
                    // Clear owners if the contract doesn't support getOwners
                    if (DEBUG) logger.log("Contract does not have getOwners method, clearing owners array");
                    setOwners([]);
                }
            } catch (error) {
                logger.error("Error fetching owners:", error);
                // Clear owners on error
                setOwners([]);
            }
        }
    }, [selectedContract, signer]);

    // Load contracts when chainId changes and is valid
    useEffect(() => {
        if (chainId && chainId !== '0' && !isContractLoading && !contractsLoaded) {
            // If we have a valid chainId from the wallet, use it
            loadContracts();
        } else if (!chainId && !isContractLoading && !contractsLoaded) {
            // If no chainId from wallet, try to load contracts with environment-configured chainId
            // This allows reading contracts without a wallet connection
            const defaultChainId = import.meta.env.VITE_CHAIN_ID || '8453'; // Default to Base mainnet
            log(`No chainId from wallet, attempting to load contracts with chainId ${defaultChainId} from environment`);

            // Create a modified version of loadContracts that uses the default chainId
            const loadWithDefaultChain = async () => {
                try {
                    log('Loading contracts with default chainId:', defaultChainId);
                    setIsContractLoading(true);
                    setIsLoading(true);
                    setError(null);
                    setLoadStartTime(Date.now());

                    const details = await loadDeploymentDetails(defaultChainId);

                    if (!details) {
                        log('No deployment details found for default chainId');
                        setError('No deployment details found. Please connect your wallet.');
                        setIsLoading(false);
                        setIsContractLoading(false);
                        return;
                    }

                    setDeploymentDetails(details);

                    const loadedContracts: Contract[] = [];

                    // Set MemerCore address if available
                    if (details.core?.memerCore?.addressForUI) {
                        setMemerCoreAddress(details.core.memerCore.addressForUI);
                    }

                    // Memer ecosystem contracts only (require ABIs)
                    const memerEcosystemContracts = {
                        ...details.multisigs,
                        ...details.core,
                        ...details.proxied,
                        ...(details.liquidity || {}),
                        ...(details.helpers || {}),
                        ...(details.charity || {})
                    };

                    // DEXs are handled separately as protocol addresses only

                    const contractEntries = Object.entries(memerEcosystemContracts);
                    log('Total Contracts to Load (default chain):', contractEntries.length);

                    // Process contracts in batches
                    const BATCH_SIZE = 5;
                    for (let i = 0; i < contractEntries.length; i += BATCH_SIZE) {
                        const batch = contractEntries.slice(i, i + BATCH_SIZE);

                        // Process each batch in parallel
                        const batchResults = await Promise.all(
                            batch.map(async ([name, entry]: [string, any]) => {
                                if (!entry.addressForUI) {
                                    return null;
                                }

                                const abi = await loadAbi(entry.abi, entry.addressForABI, defaultChainId);

                                if (abi && Array.isArray(abi)) {
                                    const contractType = getCategoryForContract(name);
                                    return {
                                        name,
                                        address: entry.addressForUI,
                                        abi,
                                        type: contractType
                                    };
                                }
                                return null;
                            })
                        );

                        // Add valid contracts to the result
                        loadedContracts.push(...batchResults.filter(Boolean) as Contract[]);

                        // Update contracts state after first batch
                        if (i === 0 && loadedContracts.length > 0) {
                            setContracts([...loadedContracts]);
                            if (!selectedContract && loadedContracts.length > 0) {
                                setSelectedContract(loadedContracts[0]);
                            }
                        }
                    }

                    log(`Loaded ${loadedContracts.length} contracts with default chainId`);
                    setContracts(loadedContracts);
                    setContractsLoaded(true);

                    // Set loading to false as soon as we have some contracts
                    if (loadedContracts.length > 0) {
                        setIsLoading(false);
                        setIsContractLoading(false);
                    } else {
                        setError('No contracts found. Please connect your wallet.');
                        setIsLoading(false);
                        setIsContractLoading(false);
                    }
                } catch (err: any) {
                    logger.error("Error loading contracts with default chainId:", err.message);
                    setError(err.message || "Failed to load contracts");
                    setIsLoading(false);
                    setIsContractLoading(false);
                }
            };

            // Execute the modified function
            loadWithDefaultChain();

            // Also set a timeout as a fallback
            const timeoutId = setTimeout(() => {
                if (isLoading) {
                    forceLoadingComplete();
                }
            }, CONTRACT_LOADING_TIMEOUT);

            return () => clearTimeout(timeoutId);
        }
    }, [chainId, loadContracts, isContractLoading, isLoading, forceLoadingComplete, contractsLoaded, loadDeploymentDetails, selectedContract]);

    // Set a global timeout to prevent infinite loading
    useEffect(() => {
        if (isLoading && loadStartTime) {
            const timeoutId = setTimeout(() => {
                if (isLoading && Date.now() - loadStartTime > CONTRACT_LOADING_TIMEOUT) {
                    forceLoadingComplete();
                }
            }, CONTRACT_LOADING_TIMEOUT + 1000); // Add 1s buffer

            return () => clearTimeout(timeoutId);
        }
    }, [isLoading, loadStartTime, forceLoadingComplete]);

    // Fetch owners when selectedContract changes and is valid
    useEffect(() => {
        if (selectedContract && signer) {
            fetchOwnersFromContract();
        }
    }, [selectedContract, signer, fetchOwnersFromContract]);

    // Use useMemo for stable references
    const contractsValue = useMemo(() => ({
        contracts,
        deploymentDetails,
        isLoading,
        error,
        memerCoreAddress,
        selectedContract,
        setSelectedContract,
        owners,
        protocolAddresses,
        loadContracts,
        fetchOwnersFromContract,
        forceLoadingComplete
    }), [
        contracts,
        deploymentDetails,
        isLoading,
        error,
        memerCoreAddress,
        selectedContract,
        owners,
        protocolAddresses,
        loadContracts,
        fetchOwnersFromContract,
        forceLoadingComplete
    ]);

    return (
        <ContractContext.Provider value={contractsValue}>
            {children}
        </ContractContext.Provider>
    );
};

export const useContracts = () => {
    const context = useContext(ContractContext);
    if (context === undefined) {
        throw new Error('useContracts must be used within a ContractProvider');
    }
    return context;
};
